import type { PropsWithChildren } from 'react';
import { PermissionsGate } from '@frameio/permissions';
import type { PermissionsGateProps } from '@frameio/permissions/components/PermissionsGate/PermissionsGate';
import { useActiveProjectId } from '@frameio/shared/contexts';
import { useGetActiveUserId } from '@frameio/shared/hooks/useGetActiveUserId';
import { useTrack } from '@frameio/shared/segment/hooks';
import { useCreateShare } from '@frameio/shared/shares/hooks';
import { MenuButton, MenuGroup } from '@frameio/vapor';
import {
  IconDuplicate,
  IconEye,
  IconShare,
  IconTextCursor,
  IconTrash,
} from '@frameio/vapor-icons';
import { CollectionPrivacySelectorMenu } from './CollectionPrivacySelector';
import TEXT from './generated/TEXT/ContextMenuOptionsForCollection';

type ConditionalPermissionsGateProps = {
  /**
   * Gives an option to pass an extra rendering condition on top of the permissions check
   */
  and?: boolean;
  /**
   * If true, the PermissionsGate will be skipped and the children will be rendered
   */
  skip?: boolean;
} & PermissionsGateProps;

/**
 * Enhanched PermissionsGate component that allows to pass an extra rendering condition on top of the permissions check
 * @param and - Gives an option to pass an extra rendering condition on top of the permissions check
 * @param skip - If true, the PermissionsGate will be skipped and the children will be rendered
 */
const ConditionalPermissionsGate = ({
  and,
  children,
  skip,
  ...permissionsGateProps
}: PropsWithChildren<ConditionalPermissionsGateProps>) => {
  if (skip) {
    return children;
  }

  if (and === undefined) {
    return (
      <PermissionsGate {...permissionsGateProps}>{children}</PermissionsGate>
    );
  }

  return (
    and && (
      <PermissionsGate {...permissionsGateProps}>{children}</PermissionsGate>
    )
  );
};

type Props = {
  collectionId: string;
  collectionCreatorId: string;
  isCollectionPrivate: boolean;
  onDeleteClick?: () => void;
  onDuplicateClick?: () => void;
  onRenameClick: () => void;
};

export const ContextMenuOptionsForCollection = ({
  collectionId,
  isCollectionPrivate,
  onDeleteClick,
  onDuplicateClick,
  onRenameClick,
  collectionCreatorId,
}: Props) => {
  const { createShareFromCollection } = useCreateShare();

  const track = useTrack();

  const projectId = useActiveProjectId();
  const activeUserId = useGetActiveUserId();

  return (
    <>
      <MenuGroup>
        <MenuButton
          iconBefore={<IconDuplicate />}
          onPress={onDuplicateClick}
          shouldCloseMenuOnSelect={true}
        >
          {TEXT.labelDuplicate}
        </MenuButton>
        <PermissionsGate check="canShare" checkOn="Project" id={projectId}>
          <MenuButton
            iconBefore={<IconShare />}
            onPress={() => {
              const share = createShareFromCollection({ collectionId });
              if (!share) {
                return;
              }

              track(
                'collection-shared',
                { collection_id: collectionId },
                { logToDatadog: true }
              );
            }}
            shouldCloseMenuOnSelect={true}
          >
            {TEXT.labelShare}
          </MenuButton>
        </PermissionsGate>

        <ConditionalPermissionsGate
          check="canUpdateCollection"
          checkOn="Project"
          id={projectId}
          skip={isCollectionPrivate}
        >
          <MenuButton iconBefore={<IconTextCursor />} onPress={onRenameClick}>
            {TEXT.labelRename}
          </MenuButton>
        </ConditionalPermissionsGate>
      </MenuGroup>

      <ConditionalPermissionsGate
        and={collectionCreatorId === activeUserId}
        check="canUpdateCollection"
        checkOn="Project"
        id={projectId}
      >
        <MenuGroup>
          <MenuButton
            iconBefore={<IconEye />}
            submenu={
              <CollectionPrivacySelectorMenu
                collectionId={collectionId}
                isPrivate={isCollectionPrivate}
                privateText={TEXT.privateCollectionText}
                publicText={TEXT.publicCollectionText}
              />
            }
          >
            {TEXT.labelChangeVisibility}
          </MenuButton>
        </MenuGroup>
      </ConditionalPermissionsGate>

      <ConditionalPermissionsGate
        check="canDeleteCollection"
        checkOn="Project"
        id={projectId}
        skip={isCollectionPrivate}
      >
        <MenuGroup>
          <MenuButton
            iconBefore={<IconTrash />}
            onPress={onDeleteClick}
            shouldCloseMenuOnSelect={true}
            variant="destructive"
          >
            {TEXT.labelDelete}
          </MenuButton>
        </MenuGroup>
      </ConditionalPermissionsGate>
    </>
  );
};

TEXT.DEFINE({
  /** Menu item label for deleting a collection */
  labelDelete: 'Delete',
  /** Menu item label for duplicating a collection */
  labelDuplicate: 'Duplicate',
  /** Menu item label for renaming a collection */
  labelRename: 'Rename',
  /** Menu item label for sharing a collection */
  labelShare: 'Share',
  /** Menu item label for changing collection visibility */
  labelChangeVisibility: 'Change Visibility',
  privateCollectionText: {
    /** Label for private collection visibility option */
    label: 'Private Collection',
    /** Description for private collection visibility option */
    details: 'Only you can see and edit this Collection',
  },
  publicCollectionText: {
    /** Label for team collection visibility option */
    label: 'Team Collection',
    /** Description for team collection visibility option */
    details: 'All Project members can see and edit this Collection',
  },
  /** Tooltip explaining that shares use grid layout */
  shareListViewDisclaimerTooltip:
    'List view is not currently available for Shares. This collection will be shared with a Grid layout.',
});
